import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { FileText, PenLine } from 'lucide-react';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { buildSignatureHtml, replaceSignatureBlock } from '../../lib/editorSafeHtml';
import { resolveSigVars } from '../../lib/signatureMergeVars';
import { findSignature, isOrgOverride } from '../../lib/signatureSelection';
import { sharedItemLabel, splitByOwner } from '../../lib/sharedItems';

/**
 * TemplateSignatureSelectors — the Template and Signature pickers, once.
 *
 * Extracted for issue #597 so every mail composer — unified reply, personal
 * reply, both compose pages — gets the same behaviour instead of a fourth and
 * fifth divergent copy. `ContactEmailComposer` (#510) is the extraction base:
 * it was the only one of the three existing copies that got replace-not-stack
 * and None-removes right.
 *
 * ## Purely presentational — it calls NO data hooks
 *
 * `useState`/`useRef`/`useCallback` are fine; `useEmailTemplates`/`useSignatures`
 * are not. This renders inside `ReplyComposer`, which renders inside
 * `messagesWithBodies.map(...)` — once per message in a thread. A data hook here
 * would violate the rules of hooks, which `craco.config.js` sets to `error`, so
 * it would fail `yarn build` (the Docker image build) even though the repo has
 * no standalone lint gate. It would also multiply each hook's mount-time
 * `invalidateQueries` by the thread length. The caller fetches; this renders.
 *
 * ## Why the CALLER owns the inserted signature
 *
 * `ContactEmailComposer` keeps "the HTML we last inserted" in an internal
 * `lastSigRef` and gets away with it because its seed and its ref live in the
 * same component. This component cannot copy that: the seed must happen in the
 * caller's open handler (the caller is what clears the body when a composer
 * opens), so an internal ref would mount empty while the body already held a
 * seeded signature — the first switch would find nothing to strip and APPEND.
 * Two signatures, on switch one.
 *
 * Two facts settle the ownership rather than leaving it to taste:
 *
 *  1. the send guard that distinguishes a signature-only body from a real one
 *     runs in the caller and needs this exact value, so it has to exist there;
 *  2. once it must live in the caller, any second copy is a second source of
 *     truth — which is the defect above.
 *
 * So `signature` is a CONTROLLED prop: `{ id, html }` as one object, seeded and
 * read by the caller, written by this component on every insert, switch and
 * removal. Keeping the id and the HTML in one value makes it impossible for the
 * dropdown's selection and the body's content to disagree.
 *
 * ## Replace-not-stack is by block (#970)
 *
 * A signature is one `div[data-ncrm-signature]` block (`buildSignatureHtml`),
 * kept intact by the editor. Switching, None and the sender re-resolve all go
 * through `replaceSignatureBlock`, which finds that block in the body — so a
 * caller that lost track of what it seeded (a draft restore) still replaces
 * instead of stacking. The tracked `signature.html` is kept for the caller's
 * bookkeeping, not for matching.
 */
export default function TemplateSignatureSelectors({
    // Data, supplied by the caller's useEmailTemplates() / useSignatures().
    templates = [],
    adminTemplates = [],
    signatures = [],
    adminSignatures = [],
    activeOverrideId = null,
    // Identity for {{Sender*}} resolution.
    user,
    // The mailbox actually sending. Empty string when unresolved — NEVER
    // user.email (#487): a wrong address baked into a sent body is worse than
    // a missing one.
    senderEmail = '',
    // The editor's setter. The current body is never read here: every write
    // goes through the functional updater so this component can never clobber
    // an edit the user made between renders.
    onBodyChange,
    // Controlled: { id, html } — the signature currently inserted, and the
    // exact HTML this component last put in the body.
    signature,
    onSignatureChange,
    // Optional; only consulted when subjectMode === 'fill'.
    subject = '',
    onSubjectChange,
    // 'fill' (compose) — a template may set an untouched subject.
    // 'ignore' (reply, reply all, forward) — a template never touches it.
    subjectMode = 'ignore',
    disabled = false,
    idPrefix = 'composer',
    /**
     * `grid` (the default) — two stacked label-over-box fields. No surface
     * uses it since #659 moved the thread cards to `compact`; it is kept as
     * the component's standalone default.
     * `strip` — the design's full-page composer bar: one 42px hairline row,
     * `[icon] Template  value ⌄   [icon] Signature  value ⌄`, on the tinted
     * background that separates the message header from the editor. Same two
     * controls, same behaviour; only the chrome differs, so the behaviour above
     * is written once and neither layout can drift from the other.
     * `compact` — the strip's row inside a thread reply card (#659): 38px,
     * white, the card's 14px gutter. The hairline colour is the card's, so the
     * caller passes it as `ruleClassName` (a `border-[…]` class).
     */
    variant = 'grid',
    /** `compact` only: the row's hairline colour class. */
    ruleClassName = '',
}) {
    const selectedSignatureId = signature?.id || '';
    const insertedHtml = signature?.html || '';

    // State, not a ref: the Select is controlled by this value, so a ref would
    // leave the trigger showing the previous template after a change.
    const [templateId, setTemplateId] = useState('none');

    // The last subject WE set. A subject the user has edited is never
    // overwritten by a template, in any mode; a subject a previous template set
    // is not "edited", so switching templates still works. Tracking it here
    // rather than asking the caller for a `subjectDirty` flag keeps the contract
    // to one prop pair and means a caller cannot forget to wire it.
    const lastSetSubject = useRef(null);
    const subjectIsUserEdited = Boolean(subject) && subject !== lastSetSubject.current;

    const allSigs = useMemo(
        () => [...(signatures || []), ...(adminSignatures || [])],
        [signatures, adminSignatures],
    );

    // Explicit `senderEmail` in the deps is LOAD-BEARING. A callback memoized
    // without it captures the first render's closure — before the mailbox
    // status resolves — and bakes the CRM login email into every inserted
    // signature. That is exactly the #487 bug; see lib/signatureMergeVars.js.
    const nextSigHtml = useCallback(
        (sig) => buildSignatureHtml(sig, user, senderEmail),
        [user, senderEmail],
    );

    /**
     * F8 (#659, the #487 class): {{SenderEmail}} is resolved when a signature
     * is INSERTED. When the sending address changes afterwards — a From change
     * in the reader footer or on a compose page, or the personal lane's
     * address resolving late — the SAME signature is re-resolved in place.
     * Before this, a message whose From was switched mid-draft shipped the
     * previous mailbox's address in its signature.
     *
     * Transition-only: never on mount, because the caller seeded it. Only
     * while the body still holds a signature block, and in place rather than
     * strip-and-append: a From change must leave the signature where it is,
     * e.g. above a forwarded thread on the compose page.
     */
    const prevSenderEmail = useRef(senderEmail);
    useEffect(() => {
        if (prevSenderEmail.current === senderEmail) return;
        prevSenderEmail.current = senderEmail;
        if (!selectedSignatureId || !insertedHtml) return;
        const sig = findSignature(signatures, adminSignatures, selectedSignatureId);
        if (!sig) return;
        const nextHtml = nextSigHtml(sig);
        if (nextHtml === insertedHtml) return;
        onBodyChange(prev => replaceSignatureBlock(prev, nextHtml, { appendIfMissing: false, legacyHtml: insertedHtml }));
        onSignatureChange({ id: selectedSignatureId, html: nextHtml });
    }, [senderEmail]); // eslint-disable-line react-hooks/exhaustive-deps -- transition-only by design: it must fire on a senderEmail change and on nothing else

    /**
     * Replace-not-stack: the body's signature block is swapped for the new one
     * (or appended when there is none). Typed body text is never touched.
     * `null` removes the block — that is **None**.
     */
    const insertSignature = useCallback((sig) => {
        const html = sig ? nextSigHtml(sig) : '';
        onBodyChange(prev => replaceSignatureBlock(prev, html, { legacyHtml: insertedHtml }));
        onSignatureChange({ id: sig ? sig.signature_id : '', html });
    }, [insertedHtml, nextSigHtml, onBodyChange, onSignatureChange]);

    const handleSignatureChange = useCallback((id) => {
        insertSignature(id === 'none' ? null : findSignature(signatures, adminSignatures, id));
    }, [insertSignature, signatures, adminSignatures]);

    /**
     * Applying a template replaces the body, then re-appends the CURRENTLY
     * SELECTED signature below it — not merely the default. The acceptance
     * criterion is "applying a template does not remove a signature already
     * present", and substituting the default for a deliberately chosen
     * signature removes it just as surely as dropping it.
     */
    const applyTemplate = useCallback((id) => {
        setTemplateId(id);
        if (id === 'none') return;
        const all = [...(templates || []), ...(adminTemplates || [])];
        const tpl = all.find(t => (t.template_id || t.id) === id);
        if (!tpl) return;

        if (subjectMode === 'fill' && tpl.subject && !subjectIsUserEdited && onSubjectChange) {
            const resolved = resolveSigVars(tpl.subject, user, senderEmail);
            lastSetSubject.current = resolved;
            onSubjectChange(resolved);
        }

        const rawBody = tpl.body || tpl.body_html;
        if (!rawBody) return;
        const tplHtml = resolveSigVars(rawBody, user, senderEmail);
        const sig = findSignature(signatures, adminSignatures, selectedSignatureId);
        const sigHtml = sig ? nextSigHtml(sig) : '';
        onSignatureChange({ id: sig ? sig.signature_id : '', html: sigHtml });
        onBodyChange(sigHtml ? `${tplHtml}<p></p>${sigHtml}` : tplHtml);
    }, [
        templates, adminTemplates, subjectMode, subjectIsUserEdited, onSubjectChange,
        user, senderEmail, signatures, adminSignatures, selectedSignatureId,
        nextSigHtml, onSignatureChange, onBodyChange,
    ]);

    const overrideActive = isOrgOverride(
        allSigs.find(s => s.signature_id === selectedSignatureId),
        activeOverrideId,
    );

    // `compact` is the strip's layout in a smaller row, so it shares every
    // class below except the row itself.
    const strip = variant === 'strip' || variant === 'compact';
    // One place per chrome difference. Everything above — the insert/replace
    // recipe, the org-override rules, the subject guard — is shared verbatim.
    const rootCls = variant === 'compact'
        ? `flex items-center gap-7 px-[14px] h-[38px] border-b bg-white ${ruleClassName || 'border-[#EDEBE6]'}`
        : strip
            ? 'flex items-center gap-7 px-5 h-[42px] border-b border-[#EDEBE6] bg-[#FBFAF9]'
            : 'grid grid-cols-2 gap-3';
    const fieldCls = strip ? 'flex items-center gap-[9px] min-w-0' : '';
    const labelCls = strip
        ? 'flex items-center gap-[9px] text-[12.5px] font-normal text-[#8A837E] whitespace-nowrap'
        : 'text-xs flex items-center gap-1';
    const iconCls = strip ? 'w-3.5 h-3.5 text-[#8A837E]' : 'w-3 h-3';
    const triggerCls = strip
        ? `h-auto w-auto max-w-[220px] border-0 shadow-none rounded-none bg-transparent
           px-0 py-0 gap-[7px] text-[13px] text-[#0C0A09]
           focus:ring-0 focus:ring-offset-0 [&>svg]:opacity-100 [&>svg]:text-[#8A837E]`
        : 'bg-white';

    // #955: the personal lists also carry items colleagues shared with this
    // user; show those in their own group, with the owner's name.
    const ownTemplates = splitByOwner(templates, user?.user_id);
    const ownSignatures = splitByOwner(signatures, user?.user_id);
    const groupHeaderCls = 'px-2 py-1 text-xs font-semibold text-stone-400 border-t mt-1 pt-1';

    return (
        <div className={rootCls} data-testid={`template-signature-selectors-${idPrefix}`}>
            <div className={fieldCls}>
                <Label className={labelCls}>
                    <FileText className={iconCls} /> Template
                </Label>
                <Select value={templateId} onValueChange={applyTemplate} disabled={disabled}>
                    <SelectTrigger className={triggerCls} data-testid={`template-select-${idPrefix}`}>
                        <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                        <SelectItem value="none">None (custom)</SelectItem>
                        {ownTemplates.mine.length > 0 && (
                            <>
                                <div className="px-2 py-1 text-xs font-semibold text-stone-400">My Templates</div>
                                {ownTemplates.mine.map(t => (
                                    <SelectItem key={t.template_id || t.id} value={t.template_id || t.id}>
                                        {t.name || t.title}
                                    </SelectItem>
                                ))}
                            </>
                        )}
                        {ownTemplates.shared.length > 0 && (
                            <>
                                <div className={groupHeaderCls} data-testid={`template-shared-group-${idPrefix}`}>
                                    Shared with me
                                </div>
                                {ownTemplates.shared.map(t => (
                                    <SelectItem key={t.template_id || t.id} value={t.template_id || t.id}>
                                        {sharedItemLabel(t.name || t.title, t)}
                                    </SelectItem>
                                ))}
                            </>
                        )}
                        {adminTemplates.length > 0 && (
                            <>
                                <div className="px-2 py-1 text-xs font-semibold text-stone-400 border-t mt-1 pt-1">
                                    Org Templates
                                </div>
                                {adminTemplates.map(t => (
                                    <SelectItem key={t.template_id || t.id} value={t.template_id || t.id}>
                                        {t.name || t.title}
                                    </SelectItem>
                                ))}
                            </>
                        )}
                        {templates.length === 0 && adminTemplates.length === 0 && (
                            <div className="px-2 py-2 text-xs text-stone-400">No templates yet</div>
                        )}
                    </SelectContent>
                </Select>
            </div>
            <div className={fieldCls}>
                <Label className={labelCls}>
                    <PenLine className={iconCls} /> Signature
                    {overrideActive && (
                        <span
                            className="text-[10px] font-normal text-stone-400"
                            data-testid={`signature-org-override-note-${idPrefix}`}
                        >
                            · Set by your organisation
                        </span>
                    )}
                </Label>
                {/* The override is preselected and labelled, NOT locked — the
                    control stays enabled and None stays available (decision 3). */}
                <Select
                    value={selectedSignatureId || 'none'}
                    onValueChange={handleSignatureChange}
                    disabled={disabled}
                >
                    <SelectTrigger className={triggerCls} data-testid={`signature-select-${idPrefix}`}>
                        <SelectValue placeholder="None" />
                    </SelectTrigger>
                    <SelectContent>
                        <SelectItem value="none">None</SelectItem>
                        {ownSignatures.mine.length > 0 && (
                            <>
                                <div className="px-2 py-1 text-xs font-semibold text-stone-400">My Signatures</div>
                                {ownSignatures.mine.map(s => (
                                    <SelectItem key={s.signature_id} value={s.signature_id}>
                                        {s.name}{s.is_default ? ' (default)' : ''}
                                    </SelectItem>
                                ))}
                            </>
                        )}
                        {ownSignatures.shared.length > 0 && (
                            <>
                                <div className={groupHeaderCls} data-testid={`signature-shared-group-${idPrefix}`}>
                                    Shared with me
                                </div>
                                {ownSignatures.shared.map(s => (
                                    <SelectItem key={s.signature_id} value={s.signature_id}>
                                        {sharedItemLabel(s.name, s)}
                                    </SelectItem>
                                ))}
                            </>
                        )}
                        {adminSignatures.length > 0 && (
                            <>
                                <div className="px-2 py-1 text-xs font-semibold text-stone-400 border-t mt-1 pt-1">
                                    Org Signatures
                                </div>
                                {adminSignatures.map(s => (
                                    <SelectItem key={s.signature_id} value={s.signature_id}>
                                        {s.name}{s.is_default ? ' (default)' : ''}
                                    </SelectItem>
                                ))}
                            </>
                        )}
                        {signatures.length === 0 && adminSignatures.length === 0 && (
                            <div className="px-2 py-2 text-xs text-stone-400">No signatures yet</div>
                        )}
                    </SelectContent>
                </Select>
            </div>
        </div>
    );
}
